import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppliedEvaluation } from '../../../../../helpers/hooks/useEvaluation';
import {
  specialTypeExists,
  typeOfLesionsExist,
  createLesionObjects,
  createMergedLesionsByCategory,
  getLesionConfig,
  categoryExists,
} from './DiagnosticsSummary.helpers';
import { useMissedFindings } from '../../../../../helpers/hooks/useMissedFindings';
import { getMissedChip } from '../../../../../helpers/diagnostics/detectionOrigin';

//Custom hook for managing diagnostics data and calculations

export const useDiagnosticsData = (t, theme, zoomedProjection) => {
  // Store state
  const results = useSelector((state) => state.results);
  const { microcalcOverlaysToShow, opacitiesOverlaysToShow } = useSelector((state) => state.images);
  const {
    applied_opacities,
    applied_microcalc,
    confirmed_opacities,
    confirmed_microcalc,
    shown_microcalc,
    shown_opacities,
  } = results;

  // Check if data is still loading
  const isDataLoading = useMemo(() => {
    if (!results || !results.exam_id) return true;

    if (!results.evaluations || Object.keys(results.evaluations).length === 0) return true;

    const hasAnyEvaluationData = Object.values(results.evaluations).some(
      (evaluation) => evaluation && Object.keys(evaluation).length > 0,
    );

    if (!hasAnyEvaluationData) return true;

    return false;
  }, [results]);

  // Derived state
  const microcalcShown = shown_microcalc || applied_microcalc;
  const opacitiesShown = shown_opacities || applied_opacities;

  // Evaluation results
  const microcalcificationsResult = useAppliedEvaluation('microcalc', microcalcShown, false);
  const opacitiesResult = useAppliedEvaluation('opacities', opacitiesShown, false);
  const { showMissed } = useMissedFindings();

  const missedChip = useMemo(() => getMissedChip(opacitiesResult), [opacitiesResult]);

  // Zoomed data
  const microcalcLesionsZoomed = microcalcificationsResult?.[zoomedProjection?.toLowerCase()];
  const opacitiesLesionsZoomed = opacitiesResult?.[zoomedProjection?.toLowerCase()];

  // Configuration
  const lesionConfig = useMemo(
    () =>
      getLesionConfig(
        microcalcificationsResult,
        opacitiesResult,
        microcalcOverlaysToShow,
        opacitiesOverlaysToShow,
        microcalcLesionsZoomed,
        opacitiesLesionsZoomed,
      ),
    [
      microcalcificationsResult,
      opacitiesResult,
      microcalcOverlaysToShow,
      opacitiesOverlaysToShow,
      microcalcLesionsZoomed,
      opacitiesLesionsZoomed,
    ],
  );

  // Generate existence checks
  const { benignExists: microcalcBenignExist, malignantExists: microcalcMalignantExist } = useMemo(
    () =>
      typeOfLesionsExist(
        lesionConfig.microcalc.benignTypes,
        lesionConfig.microcalc.malignantTypes,
        microcalcificationsResult,
        zoomedProjection,
        showMissed,
      ),
    [
      lesionConfig.microcalc.benignTypes,
      lesionConfig.microcalc.malignantTypes,
      microcalcificationsResult,
      zoomedProjection,
      showMissed,
    ],
  );

  const { benignExists: opacitiesBenignExist, malignantExists: opacitiesMalignantExist } = useMemo(
    () =>
      typeOfLesionsExist(
        lesionConfig.opacities.benignTypes,
        lesionConfig.opacities.malignantTypes,
        opacitiesResult,
        zoomedProjection,
        showMissed,
      ),
    [
      lesionConfig.opacities.benignTypes,
      lesionConfig.opacities.malignantTypes,
      opacitiesResult,
      zoomedProjection,
      showMissed,
    ],
  );

  const vesselsExist = useMemo(
    () => specialTypeExists('vessel', microcalcificationsResult, zoomedProjection),
    [microcalcificationsResult, zoomedProjection],
  );

  const lesionKnownExist = useMemo(
    () => specialTypeExists('lesionKnown', opacitiesResult, zoomedProjection),
    [opacitiesResult, zoomedProjection],
  );

  const lesionKnownShown = useMemo(
    () => Object.values(opacitiesOverlaysToShow?.lesionKnown || {}).some((value) => value === true),
    [opacitiesOverlaysToShow],
  );

  const microcalcVesselsShown = useMemo(
    () => Object.values(microcalcOverlaysToShow?.vessel || {}).some((value) => value === true),
    [microcalcOverlaysToShow],
  );

  const otherLesions = useMemo(() => {
    const lesions = [
      {
        key: 'vessel',
        label: t('ResultView.vessels'),
        tooltip: t('diagnose.vessels_calc'),
        module: 'calc',
        exist: vesselsExist,
        color: theme.palette.diagnosticsClasses.vessel,
        isShown: microcalcVesselsShown,
        overlaysToShow: 'vessel',
      },
      {
        key: 'missed',
        label: t(missedChip.missedChipLabelKeys.short),
        tooltip: t(missedChip.missedChipLabelKeys.explanation),
        module: 'missed',
        exist: missedChip.missedExist,
        color: theme.palette.diagnosticsClasses.priorFinding,
        isShown: showMissed,
        overlaysToShow: 'missed',
      },
    ];

    if (lesionKnownExist) {
      lesions.push({
        key: 'lesionKnown',
        label: t('projections.lesionKnown'),
        tooltip: t('projections.lesionKnownHelper'),
        module: 'opac',
        exist: lesionKnownExist,
        color: theme.palette.diagnosticsClasses.lesionKnown,
        isShown: lesionKnownShown,
        overlaysToShow: 'lesionKnown',
      });
    }

    return lesions;
  }, [
    t,
    theme,
    vesselsExist,
    microcalcVesselsShown,
    missedChip,
    showMissed,
    lesionKnownExist,
    lesionKnownShown,
  ]);

  // Generate lesion objects
  const microcalcLesions = useMemo(
    () => createLesionObjects(lesionConfig.microcalc, 'microcalc', t, theme, zoomedProjection, false),
    [lesionConfig.microcalc, t, theme, zoomedProjection],
  );

  const opacitiesLesions = useMemo(
    () => createLesionObjects(lesionConfig.opacities, 'opacities', t, theme, zoomedProjection, showMissed),
    [lesionConfig.opacities, t, theme, zoomedProjection, showMissed],
  );

  // Create merged lesions structure
  const mergedLesionsByCategory = useMemo(
    () => createMergedLesionsByCategory(microcalcLesions, opacitiesLesions, t),
    [microcalcLesions, opacitiesLesions, t],
  );

  const mergedLesions = useMemo(
    () => [...microcalcLesions, ...opacitiesLesions],
    [microcalcLesions, opacitiesLesions],
  );

  // Empty state checks
  const microcalcEmpty = useMemo(
    () => categoryExists(microcalcificationsResult),
    [microcalcificationsResult],
  );
  const opacitiesEmpty = useMemo(() => categoryExists(opacitiesResult), [opacitiesResult]);

  return {
    // Loading state
    isDataLoading,

    // State
    results,
    microcalcOverlaysToShow,
    opacitiesOverlaysToShow,
    showMissed,
    applied_opacities,
    applied_microcalc,
    confirmed_opacities,
    confirmed_microcalc,
    shown_microcalc,
    shown_opacities,
    microcalcShown,
    opacitiesShown,

    // Results
    microcalcificationsResult,
    opacitiesResult,

    // Zoomed data
    microcalcLesionsZoomed,
    opacitiesLesionsZoomed,

    // Existence checks
    microcalcBenignExist,
    microcalcMalignantExist,
    opacitiesBenignExist,
    opacitiesMalignantExist,

    // Lesion objects
    microcalcLesions,
    opacitiesLesions,
    mergedLesionsByCategory,
    mergedLesions,
    otherLesions,

    // Empty states
    microcalcEmpty,
    opacitiesEmpty,
  };
};
